import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';

import {
    USERMINFO,
    MY_WORKSPACES,
    REGISTER_USER,
    REQUEST_INVITE,
    EMAIL_PREFERENCES_ALL,
    EMAIL_PREFERENCES_PROJECT,
    AUTH_LOGOUT,
} from '../apiServices/api_routes';

export const GetUserData = createAsyncThunk('userSlice/getUserData', async () => await axiosInstance.get(USERMINFO));

export const GetMyWorkspaces = createAsyncThunk('userSlice/getMyWorkspaces', async () => await axiosInstance.get(MY_WORKSPACES));

export const GetEmailPreferences = createAsyncThunk('userSlice/getEmailPreferences', async () => {
    const data = await axiosInstance.get(EMAIL_PREFERENCES_ALL);
    return data;
});

export const UpdateProjectEmailPreferences = createAsyncThunk('userSlice/updateProjectEmailPreferences', async ({ projectId, preferences }) => {
    const data = await axiosInstance.post(EMAIL_PREFERENCES_PROJECT + projectId + '/', {
        preferences: preferences,
    });
    return data;
});

export const RegisterUser = createAsyncThunk('POST_REGISTER_USER', async body => {
    const data = await axiosInstance.post(REGISTER_USER, body);
    return data;
});

/** Sync user.has_diligence_tools from the active workspace membership (tenant-specific). */
function syncUserFlagsFromActiveMembership(state) {
    if (!state.user || typeof state.user !== 'object') return;
    const activeId = state.activeWorkspaceId;
    const membership = Array.isArray(state.memberships) ? state.memberships.find(m => m?.workspace?.id === activeId) : null;
    if (membership && typeof membership.has_diligence_tools === 'boolean') {
        state.user.has_diligence_tools = membership.has_diligence_tools;
    }
}

export const userSlice = createSlice({
    name: 'usersSlice',
    initialState: {
        user: {},
        status: null,
        access: '',
        loading: true,
        error: false,
        errorMessage: '',
        isLoggedIn: false,
        emailPreferences: {},
        emailPreferencesLoading: false,
        updateEmailPreferencesLoading: false,
        memberships: [],
        defaultWorkspaceId: null,
        activeWorkspaceId: localStorage.getItem('active_workspace_id') || '',
        workspaceSelectionRequired: localStorage.getItem('workspace_selection_required') === 'true',
        workspacesLoading: false,
    },
    reducers: {
        setAccessToken: (state, { payload }) => {
            return { ...state, access: payload };
        },
        setActiveWorkspaceId: (state, { payload }) => {
            state.activeWorkspaceId = payload || '';
            if (payload) {
                localStorage.setItem('active_workspace_id', payload);
            } else {
                localStorage.removeItem('active_workspace_id');
            }
            syncUserFlagsFromActiveMembership(state);
        },
        setWorkspaceSelectionRequired: (state, { payload }) => {
            const val = !!payload;
            state.workspaceSelectionRequired = val;
            if (val) {
                localStorage.setItem('workspace_selection_required', 'true');
            } else {
                localStorage.removeItem('workspace_selection_required');
            }
        },
        updateWorkspaceDefaults: (state, { payload }) => {
            const { workspaceId, patch } = payload || {};
            if (!workspaceId || !patch) return;
            state.memberships = state.memberships.map(m => {
                if (m?.workspace?.id === workspaceId) {
                    return {
                        ...m,
                        workspace: {
                            ...(m.workspace || {}),
                            ...patch,
                        },
                    };
                }
                return m;
            });
            syncUserFlagsFromActiveMembership(state);
        },
        logout: state => {
            for (var key in localStorage) {
                if (localStorage.hasOwnProperty(key) && key !== 'workstream' && key !== 'mobileDeviceWarn') {
                    localStorage.removeItem(key);
                }
            }
        },
    },
    extraReducers: {
        [GetUserData.pending]: (state, { payload }) => {
            state.user = payload?.data;
            state.loading = true;
        },
        [GetUserData.fulfilled]: (state, { payload }) => {
            localStorage.setItem('user_id', payload?.data[0]?.id);
            // localStorage.setItem('userType', payload?.data[0]?.types);
            state.user = { ...payload?.data[0] };
            state.isLoggedIn = true;
            state.error = false;
            state.errorMessage = '';
            state.loading = false;
            syncUserFlagsFromActiveMembership(state);
        },
        [GetUserData.rejected]: (state, action) => {
            state.error = true;
            state.errorMessage = action?.error?.message;
            state.user = {};
            state.loading = false;
        },

        // GetMyWorkspaces
        [GetMyWorkspaces.pending]: state => {
            state.workspacesLoading = true;
        },
        [GetMyWorkspaces.fulfilled]: (state, { payload }) => {
            const data = payload?.data || {};
            state.defaultWorkspaceId = data?.default_workspace_id || null;
            state.memberships = Array.isArray(data?.memberships) ? data.memberships : [];
            state.workspacesLoading = false;

            // If the current activeWorkspaceId no longer exists in memberships, clear it.
            if (state.activeWorkspaceId && !state.memberships.some(m => m?.workspace?.id === state.activeWorkspaceId)) {
                state.activeWorkspaceId = '';
                localStorage.removeItem('active_workspace_id');
            }

            // If active workspace not set, prefer default, else auto-select when only one workspace.
            if (!state.activeWorkspaceId && !state.workspaceSelectionRequired) {
                if (state.defaultWorkspaceId) {
                    state.activeWorkspaceId = state.defaultWorkspaceId;
                    localStorage.setItem('active_workspace_id', state.defaultWorkspaceId);
                } else if (state.memberships.length === 1) {
                    const onlyWorkspaceId = state.memberships?.[0]?.workspace?.id;
                    if (onlyWorkspaceId) {
                        state.activeWorkspaceId = onlyWorkspaceId;
                        localStorage.setItem('active_workspace_id', onlyWorkspaceId);
                    }
                }
            }
            syncUserFlagsFromActiveMembership(state);
        },
        [GetMyWorkspaces.rejected]: (state, action) => {
            state.workspacesLoading = false;
        },

        //RegisterUser
        [RegisterUser.pending]: (state, { payload }) => {
            state.status = 'loading';
        },
        [RegisterUser.fulfilled]: (state, { payload }) => {
            state.status = 'success';
        },
        [RegisterUser.rejected]: (state, action) => {
            state.status = 'rejected';
        },

        //GetEmailPreferences
        [GetEmailPreferences.pending]: (state, { payload }) => {
            state.emailPreferencesLoading = true;
        },
        [GetEmailPreferences.fulfilled]: (state, { payload }) => {
            state.emailPreferences = payload?.data || {};
            state.emailPreferencesLoading = false;
        },
        [GetEmailPreferences.rejected]: (state, action) => {
            state.emailPreferencesLoading = false;
        },

        //UpdateProjectEmailPreferences
        [UpdateProjectEmailPreferences.pending]: (state, { payload }) => {
            state.updateEmailPreferencesLoading = true;
        },
        [UpdateProjectEmailPreferences.fulfilled]: (state, { payload }) => {
            // Update the specific project's preferences in the existing data
            if (state.emailPreferences?.project_preferences && payload?.data?.project_id) {
                const projectIndex = state.emailPreferences.project_preferences.findIndex(project => project.project_id === payload.data.project_id);

                if (projectIndex !== -1) {
                    // Merge changed preferences with existing preferences
                    state.emailPreferences.project_preferences[projectIndex] = {
                        ...state.emailPreferences.project_preferences[projectIndex],
                        preferences: {
                            ...state.emailPreferences.project_preferences[projectIndex].preferences,
                            ...payload.data.preferences,
                        },
                        last_updated: payload.data.last_updated,
                    };
                } else {
                    // Add new project preferences if not found
                    state.emailPreferences.project_preferences.push({
                        project_id: payload.data.project_id,
                        project_name: payload.data.project_name,
                        preferences: payload.data.preferences,
                        last_updated: payload.data.last_updated,
                        has_preferences: true,
                    });
                }
            }
            state.updateEmailPreferencesLoading = false;
        },
        [UpdateProjectEmailPreferences.rejected]: (state, action) => {
            state.updateEmailPreferencesLoading = false;
        },
    },
});

export const { setAccessToken, setActiveWorkspaceId, setWorkspaceSelectionRequired, updateWorkspaceDefaults, logout } =
    userSlice.actions;

/** Notify API of logout for workspace audit, then clear client session (always clears even if POST fails). */
export const logoutWithAudit = createAsyncThunk('usersSlice/logoutWithAudit', async (_, { dispatch }) => {
    try {
        await axiosInstance.post(AUTH_LOGOUT);
    } catch (e) {
        // No access token, expired session, or network — still sign out locally
    }
    dispatch(logout());
});

export default userSlice.reducer;
